import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";import axios from "axios";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Circle, XCircle, ChevronLeft, ChevronRight, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/lib/toastBanner";
import { logEvent } from "@/lib/events";
import { sanitizeHtml } from "@/utils/sanitize";
import LexpErrorBoundary from "@/components/LexpErrorBoundary";
import FitToPanel from "@/components/FitToPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function McItem({ item, onSubmit }) {
  const [selected, setSelected] = useState(item.response?.selected_option_ids?.[0] || null);
  const [submitted, setSubmitted] = useState(!!item.response);
  const [feedback, setFeedback] = useState(item.response ? { correct: item.response.correct, correct_option_ids: [], earned_points: item.response.earned_points } : null);
  const [submitting, setSubmitting] = useState(false);
  // Sync local state when the parent's `item.response` updates (e.g., after
  // a successful submit propagates back through `setItems`). Without this,
  // a transient client-side issue that wipes local state would leave the
  // learner staring at an un-acknowledged submission until a hard refresh.
  useEffect(() => {
    if (item.response) {
      setSubmitted(true);
      setFeedback((prev) => ({
        correct: item.response.correct,
        earned_points: item.response.earned_points,
        correct_option_ids: prev?.correct_option_ids || [],
      }));
      if (item.response.selected_option_ids?.[0]) {
        setSelected(item.response.selected_option_ids[0]);
      }
    }
  }, [item.response]);
  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const result = await onSubmit({ selected_option_ids: [selected] });
      setFeedback(result);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your answer. Please try again.");
    } finally { setSubmitting(false); }
  };
  return (
    <div className="space-y-3" data-testid={`mc-item-${item.id}`}>
      <div className="text-sm text-gray-800 font-body ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.stem) }} />
      <div className="space-y-2">
        {(item.answer_options || []).map((opt) => {
          const isSelected = selected === opt.option_id;
          const isCorrect = feedback?.correct_option_ids?.includes(opt.option_id);
          return (
            <button key={opt.option_id} type="button" disabled={submitted}
              onClick={() => setSelected(opt.option_id)}
              className={`w-full text-left px-4 py-3 rounded-lg border text-sm font-body transition-all ${
                submitted
                  ? isCorrect ? "border-green-300 bg-green-50 text-green-800"
                    : isSelected && !feedback?.correct ? "border-red-300 bg-red-50 text-red-800"
                    : "border-gray-200 text-gray-600"
                  : isSelected ? "border-blue-400 bg-blue-50 text-blue-800" : "border-gray-200 hover:border-gray-300 text-gray-700"
              }`}
            >
              {opt.text}
            </button>
          );
        })}
      </div>
      {!submitted && (
        <Button onClick={handleSubmit} disabled={!selected || submitting} size="sm"
          className="font-body" style={{ backgroundColor: "var(--primary-color, #4A90D9)" }}>
          {submitting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}Submit
        </Button>
      )}
      {submitted && feedback && (
        <div className={`flex items-center gap-2 text-sm font-body ${feedback.correct ? "text-green-600" : "text-red-500"}`}>
          {feedback.correct ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
          {feedback.correct ? "Correct!" : "Incorrect"}
          {feedback.earned_points != null && <span className="text-gray-400 ml-1">({feedback.earned_points} pts)</span>}
        </div>
      )}
    </div>
  );
}

function SataItem({ item, onSubmit }) {
  const [selected, setSelected] = useState(new Set(item.response?.selected_option_ids || []));
  const [submitted, setSubmitted] = useState(!!item.response);
  const [feedback, setFeedback] = useState(item.response ? { correct: item.response.correct } : null);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (item.response) {
      setSubmitted(true);
      setFeedback((prev) => ({ ...(prev || {}), correct: item.response.correct, earned_points: item.response.earned_points }));
      if (item.response.selected_option_ids?.length) {
        setSelected(new Set(item.response.selected_option_ids));
      }
    }
  }, [item.response]);
  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const result = await onSubmit({ selected_option_ids: [...selected] });
      setFeedback(result);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your answer. Please try again.");
    } finally { setSubmitting(false); }
  };
  return (
    <div className="space-y-3" data-testid={`sata-item-${item.id}`}>
      <div className="text-sm text-gray-800 font-body ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.stem) }} />
      <div className="space-y-2">
        {(item.answer_options || []).map((opt) => (
          <button key={opt.option_id} type="button" disabled={submitted}
            onClick={() => toggle(opt.option_id)}
            className={`w-full text-left px-4 py-3 rounded-lg border text-sm font-body transition-all ${
              selected.has(opt.option_id) ? "border-blue-400 bg-blue-50 text-blue-800" : "border-gray-200 hover:border-gray-300 text-gray-700"
            } ${submitted ? "opacity-70" : ""}`}
          >
            {opt.text}
          </button>
        ))}
      </div>
      {!submitted && (
        <Button onClick={handleSubmit} disabled={selected.size === 0 || submitting} size="sm"
          className="font-body" style={{ backgroundColor: "var(--primary-color, #4A90D9)" }}>
          {submitting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}Submit
        </Button>
      )}
      {submitted && feedback && (
        <div className={`flex items-center gap-2 text-sm font-body ${feedback.correct ? "text-green-600" : "text-amber-500"}`}>
          {feedback.correct ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
          {feedback.correct ? "Correct!" : "Partial/Incorrect"}
        </div>
      )}
    </div>
  );
}

function LikertItem({ item, onSubmit }) {
  const [selected, setSelected] = useState(
    item.response?.likert_value != null
      ? Number(item.response.likert_value)
      : item.response?.selected_option_ids?.[0] != null
      ? Number(item.response.selected_option_ids[0])
      : null
  );
  const [submitted, setSubmitted] = useState(!!item.response);
  const [submitting, setSubmitting] = useState(false);
  const min = item.likert_scale_min ?? 0;
  const max = item.likert_scale_max ?? 5;
  const range = [];
  for (let i = min; i <= max; i++) range.push(i);
  const handleSubmit = async (val) => {
    setSelected(val);
    setSubmitting(true);
    try {
      // Send BOTH `likert_value` (canonical, used by analytics/CFI rollups)
      // and `selected_option_ids` (legacy back-compat for older readers).
      await onSubmit({ likert_value: val, selected_option_ids: [String(val)] });
      setSubmitted(true);
    } finally { setSubmitting(false); }
  };
  return (
    <div className="space-y-3" data-testid={`likert-item-${item.id}`}>
      <div className="text-sm text-gray-800 font-body ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.stem) }} />
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${range.length}, minmax(0, 1fr))` }}>
        {range.map((val) => (
          <button key={val} type="button" disabled={submitted || submitting}
            onClick={() => !submitted && handleSubmit(val)}
            data-testid={`likert-option-${val}`}
            className={`flex flex-col items-center justify-start gap-2 px-2 py-3 rounded-lg border font-body transition-all min-h-[120px] ${
              selected === val ? "border-blue-400 bg-blue-50 text-blue-800" : "border-gray-200 hover:border-gray-300 text-gray-700"
            } ${submitted ? "opacity-70" : ""}`}
          >
            <span className="text-2xl font-bold leading-none">{val}</span>
            {item.likert_labels?.[String(val)] && (
              <span className="text-sm text-gray-700 text-center leading-tight whitespace-normal break-words">{item.likert_labels[String(val)]}</span>
            )}
          </button>
        ))}
      </div>
      {submitted && <p className="text-xs text-green-600 font-body flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Response recorded</p>}
    </div>
  );
}

function TaskItem({ item, onSubmit }) {
  const [completed, setCompleted] = useState(item.response?.task_completed || false);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (item.response?.task_completed) setCompleted(true);
  }, [item.response]);
  const handleComplete = async () => {
    setSubmitting(true);
    try {
      await onSubmit({ task_completed: true });
      setCompleted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark task complete. Please try again.");
    } finally { setSubmitting(false); }
  };
  return (
    <div className="space-y-3" data-testid={`task-item-${item.id}`}>
      <div className="text-sm text-gray-800 font-body ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.stem) }} />
      {completed ? (
        <p className="text-sm text-green-600 font-body flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Completed</p>
      ) : (
        <Button onClick={handleComplete} disabled={submitting} size="sm"
          className="font-body" style={{ backgroundColor: "var(--primary-color, #4A90D9)" }}>
          {submitting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}Mark Complete
        </Button>
      )}
    </div>
  );
}

/* ─── CFI Paired Card ──────────────────────────────────────────────────────
   Renders the CFI MC question and the system Confidence Likert as ONE
   stacked card with a single Submit button. Replaces the standard per-item
   pagination flow when `lexp.cfi_mode === true`. The learner cannot submit
   until both inputs are answered.

   On submit:
     - Fires the MC + Likert submit POSTs in sequence (each carries
       `?track_id=` so the backend stamps `cfi_track_id`).
     - Marks the LEXP complete locally AND on the server.
     - Advances: if a next LEXP exists in the track, navigates there; else
       (last LEXP of track) submits the track and routes back.
   ────────────────────────────────────────────────────────────────────────── */
function CfiPairedCard({ mcItem, likertItem, onSubmit, submitting, alreadyAnswered }) {
  const [selectedOptionId, setSelectedOptionId] = useState(
    mcItem?.response?.selected_option_ids?.[0] ?? null
  );
  const [confidence, setConfidence] = useState(
    likertItem?.response?.likert_value != null
      ? Number(likertItem.response.likert_value)
      : likertItem?.response?.selected_option_ids?.[0] != null
      ? Number(likertItem.response.selected_option_ids[0])
      : null
  );
  const min = likertItem?.likert_scale_min ?? 1;
  const max = likertItem?.likert_scale_max ?? 5;
  const range = [];
  for (let i = min; i <= max; i++) range.push(i);

  const ready = selectedOptionId != null && confidence != null;

  const submit = async () => {
    if (!ready || submitting || alreadyAnswered) return;
    await onSubmit({
      mc: { selected_option_ids: [selectedOptionId] },
      likert: { likert_value: confidence, selected_option_ids: [String(confidence)] },
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-6" data-testid="cfi-paired-card">
      {/* MC question */}
      {mcItem && (
        <div className="space-y-3" data-testid="cfi-paired-mc">
          <div className="text-base text-gray-900 font-body font-semibold ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(mcItem.stem || mcItem.title || "") }} />
          <div className="space-y-2">
            {((mcItem && mcItem.answer_options) || []).map((opt) => {
              const isSelected = selectedOptionId === opt.option_id;
              return (
                <button
                  key={opt.option_id}
                  type="button"
                  disabled={alreadyAnswered || submitting}
                  onClick={() => setSelectedOptionId(opt.option_id)}
                  data-testid={`cfi-mc-option-${opt.option_id}`}
                  className={`w-full text-left px-4 py-3 rounded-lg border font-body text-sm transition-all flex items-start gap-3 ${
                    isSelected
                      ? "border-blue-400 bg-blue-50 text-blue-900"
                      : "border-gray-200 hover:border-gray-300 text-gray-700"
                  } ${alreadyAnswered ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  <span className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 ${isSelected ? "border-blue-500 bg-blue-500" : "border-gray-300"}`} />
                  <span className="ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(opt.text) }} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-gray-100" />

      {/* Likert — full-width 5-cell grid, wrapped labels (no truncation) */}
      {likertItem && (
        <div className="space-y-3" data-testid="cfi-paired-likert">
          <div className="text-sm text-gray-800 font-body ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(likertItem.stem || "How confident are you in your answer?") }} />
          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${range.length}, minmax(0, 1fr))` }}>
            {range.map((val) => {
              const isSelected = confidence === val;
              return (
                <button
                  key={val}
                  type="button"
                  disabled={alreadyAnswered || submitting}
                  onClick={() => setConfidence(val)}
                  data-testid={`cfi-likert-${val}`}
                  className={`flex flex-col items-center justify-start gap-2 px-2 py-3 rounded-lg border font-body transition-all min-h-[120px] ${
                    isSelected ? "border-blue-400 bg-blue-50 text-blue-900" : "border-gray-200 hover:border-gray-300 text-gray-700"
                  } ${alreadyAnswered ? "opacity-70" : ""}`}
                >
                  <span className="text-2xl font-bold leading-none">{val}</span>
                  {likertItem.likert_labels?.[String(val)] && (
                    <span className="text-sm text-gray-700 text-center leading-tight whitespace-normal break-words">
                      {likertItem.likert_labels[String(val)]}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Single Submit */}
      <div className="flex justify-end pt-2">
        {alreadyAnswered ? (
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="cfi-paired-completed">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-body font-semibold text-emerald-700">Submitted</span>
          </div>
        ) : (
          <Button
            onClick={submit}
            disabled={!ready || submitting}
            data-testid="cfi-paired-submit"
            className="font-body bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
            Submit
          </Button>
        )}
      </div>
    </div>
  );
}


export default function LearnerStandaloneLexp() {
  const { lexpId } = useParams();
  const [searchParams] = useSearchParams();
  const trackId = searchParams.get("trackId");
  const navigate = useNavigate();
  const [lexp, setLexp] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeItem, setActiveItem] = useState(null);
  const [track, setTrack] = useState(null);
  const [submittingTrack, setSubmittingTrack] = useState(false);
  const [loadWarning, setLoadWarning] = useState(null);

  useEffect(() => {
    // Force-load strategy: independent requests via Promise.allSettled so a
    // single failing endpoint never blocks the page from rendering. We only
    // fall back to redirecting if the primary LEXP fetch itself fails AND
    // we have no data at all — otherwise we render what we have with a
    // non-blocking warning banner. This fixes the "LEXP won't load in prod"
    // report where a partial-failure fetch was silently kicking users out.
    let cancelled = false;
    const fetchData = async () => {
      const detailUrl = trackId
        ? `${API}/learner/lexps/${lexpId}?track_id=${trackId}`
        : `${API}/learner/lexps/${lexpId}`;
      const [lexpRes, itemsRes, trackRes] = await Promise.allSettled([
        axios.get(detailUrl, { withCredentials: true }),
        axios.get(`${API}/learner/lexps/${lexpId}/items`, { withCredentials: true }),
        trackId
          ? axios.get(`${API}/learner/tracks/${trackId}`, { withCredentials: true })
          : Promise.resolve({ data: null }),
      ]);
      if (cancelled) return;

      const warnings = [];
      if (lexpRes.status === "fulfilled") {
        setLexp(lexpRes.value.data);
      } else {
        warnings.push("lexp");
      }
      if (itemsRes.status === "fulfilled") {
        const arr = Array.isArray(itemsRes.value.data) ? itemsRes.value.data : [];
        setItems(arr);
        if (arr.length > 0) setActiveItem(arr[0].id);
      } else {
        setItems([]);
        warnings.push("items");
      }
      if (trackId) {
        if (trackRes.status === "fulfilled" && trackRes.value?.data) {
          setTrack(trackRes.value.data);
        }
        // If track fetch failed, we silently hide the nav bar (existing behavior)
      }
      if (warnings.length) {
        setLoadWarning(
          warnings.includes("lexp")
            ? "This experience couldn't load fully. Some content may be missing."
            : "Some items couldn't load. You can still work with what's shown."
        );
      }
      try {
        logEvent("standalone_lexp_viewed", {
          lexp_id: lexpId,
          lexp_title: (lexpRes.status === "fulfilled" && lexpRes.value?.data?.title) || lexpId,
          track_id: trackId || "standalone",
        });
      } catch { /* logging must never block render */ }
      setLoading(false);
    };
    fetchData();
    return () => { cancelled = true; };
  }, [lexpId, trackId, navigate]);

  const handleSubmitTrack = async () => {
    setSubmittingTrack(true);
    try {
      await axios.post(`${API}/learner/tracks/${trackId}/submit`, {}, { withCredentials: true });
      toast.success("Track submitted!");
      navigate(`/portal/tracks/${trackId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit track");
    } finally { setSubmittingTrack(false); }
  };

  const markComplete = async () => {
    try {
      const url = trackId
        ? `${API}/learner/tracks/${trackId}/lexps/${lexpId}/complete`
        : `${API}/learner/lexps/${lexpId}/complete`;
      await axios.post(url, {}, { withCredentials: true });
      logEvent("lexp_completed", { lexp_id: lexpId, lexp_title: lexp?.title || lexpId, track_id: trackId || "standalone" });
      setLexp((prev) => ({ ...prev, completed: true }));
    } catch (err) { console.error("Failed to mark complete:", err); }
  };

  const handleItemSubmit = async (itemId, body) => {
    try {
      // Pass track_id so the backend can tenant-scope CFI responses to the
      // correct (learner, track) deployment for quadrant rollup — without
      // this hint, a CFI LEXP shared across multiple tracks can't be
      // deterministically mapped back to the learner's current track.
      const url = `${API}/learner/lexps/${lexpId}/items/${itemId}/submit${trackId ? `?track_id=${trackId}` : ""}`;
      const { data } = await axios.post(url, body, { withCredentials: true });
      const updated = items.map((i) => i.id === itemId ? { ...i, response: { ...body, correct: data.correct, earned_points: data.earned_points } } : i);
      setItems(updated);
      // Auto-complete when every item has a response
      const allAnswered = updated.every((i) => !!i.response);
      if (allAnswered && lexp && !lexp.completed) {
        setLexp((prev) => ({ ...prev, completed: true }));
        markComplete();
        toast.success("All items complete!");
      }
      return data;
    } catch (err) {
      // Surface the error so the McItem/SataItem catch can render the
      // error toast and the learner isn't left wondering whether the
      // answer was saved.
      throw err;
    }
  };

  const handleCompleteAndSubmitTrack = async () => {
    if (!trackId) return;
    setSubmittingTrack(true);
    try {
      if (!lexp.completed) {
        await markComplete();
      }
      await axios.post(`${API}/learner/tracks/${trackId}/submit`, {}, { withCredentials: true });
      toast.success("Track submitted!");
      navigate(`/portal/tracks/${trackId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit track");
    } finally { setSubmittingTrack(false); }
  };

  const goBack = () => {
    if (trackId) navigate(`/portal/tracks/${trackId}`);
    else navigate("/portal/dashboard");
  };

  /* ─── CFI paired submit ──────────────────────────────────────────────────
     Fires the cfi_mc + likert submits in sequence (each carries `?track_id=`
     so the backend stamps `cfi_track_id`), marks the LEXP complete, then
     advances the learner to the next track activity. Last-LEXP-of-track =
     submit-track-and-return. Otherwise = navigate to next LEXP. ─────────── */
  const [cfiSubmitting, setCfiSubmitting] = useState(false);
  // Local UI state for the CFI paired card (controlled before submit). MUST
  // reset every time `lexpId` changes — the route between CFI LEXPs in a
  // track keeps the same component instance, so without this reset the
  // previous LEXP's selection bleeds into the next prompt and biases the
  // learner. Don't rely on item.response defaults: a fresh (unanswered)
  // LEXP will have no response and we want the cells visually clean.
  const [cfiSelection, setCfiSelection] = useState({ optionId: null, confidence: null });
  useEffect(() => {
    setCfiSelection({ optionId: null, confidence: null });
  }, [lexpId]);
  const handleCfiSubmit = async ({ mc, likert }) => {
    setCfiSubmitting(true);
    try {
      const mcItem = items.find((i) => i.item_type === "cfi_mc");
      const likertItem = items.find((i) => i.item_type === "likert");
      if (mcItem) await handleItemSubmit(mcItem.id, mc);
      if (likertItem) await handleItemSubmit(likertItem.id, likert);
      // Advance to the next track activity. Compute track flow state inline
      // so this handler stays self-contained (avoids TDZ on closure refs).
      if (trackId) {
        const trackLexpsLocal = track?.lexps || [];
        const idx = trackLexpsLocal.findIndex((l) => l.lexp_id === lexpId);
        const isLast = idx === trackLexpsLocal.length - 1 && trackLexpsLocal.length > 0;
        const next = idx >= 0 && idx < trackLexpsLocal.length - 1 ? trackLexpsLocal[idx + 1] : null;
        const submitted = !!track?.submitted;
        if (isLast && !submitted) {
          await axios.post(`${API}/learner/tracks/${trackId}/submit`, {}, { withCredentials: true });
          toast.success("Track submitted!");
          navigate(`/portal/tracks/${trackId}`);
        } else if (next) {
          navigate(`/portal/learning/${next.lexp_id}?trackId=${trackId}`);
        } else {
          navigate(`/portal/tracks/${trackId}`);
        }
      } else {
        toast.success("CFI submitted");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit CFI response");
    } finally {
      setCfiSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  if (!lexp) {
    // Force-load fallback so the learner sees an actionable message
    // instead of a blank white screen when the LEXP endpoint fails.
    return (
      <div className="max-w-2xl mx-auto my-10 rounded-xl border border-gray-200 bg-white p-6" data-testid="lexp-load-failed">
        <h2 className="font-heading font-bold text-lg text-gray-900">
          This experience couldn't be loaded
        </h2>
        <p className="text-sm text-gray-600 font-body mt-1">
          We couldn't fetch this learning experience. Please try again in a moment.
        </p>
        <div className="mt-4 flex gap-2">
          <Button variant="outline" onClick={() => window.location.reload()} data-testid="lexp-load-retry">Retry</Button>
          <Button onClick={goBack} data-testid="lexp-load-back" style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}>Back</Button>
        </div>
      </div>
    );
  }

  const active = items.find((i) => i.id === activeItem);
  // For LEXPs with NO scored items (reading-only / lecture content), there
  // is nothing to answer, so consider the learner "done" with items as soon
  // as the page loads — otherwise the Submit & Complete button never shows.
  const allAnswered = items.length === 0 || items.every((i) => !!i.response);

  // ── Track LEXP navigation (only when opened from a track) ──
  const trackLexps = track?.lexps || [];
  const currentLexpIdx = trackLexps.findIndex((l) => l.lexp_id === lexpId);
  const prevLexp = currentLexpIdx > 0 ? trackLexps[currentLexpIdx - 1] : null;
  const nextLexp = currentLexpIdx >= 0 && currentLexpIdx < trackLexps.length - 1 ? trackLexps[currentLexpIdx + 1] : null;
  const isLastLexp = currentLexpIdx === trackLexps.length - 1 && trackLexps.length > 0;
  const trackAlreadySubmitted = !!track?.submitted;
  // True when every other LEXP in the track is already complete. Used to
  // gate the bottom Submit-Track CTA so the learner can't fire it before
  // working through earlier LEXPs.
  const otherLexpsComplete = trackLexps.length > 0 && trackLexps.every((l) => l.lexp_id === lexpId || l.completed);
  const goToLexp = (lid) => {
    if (!lid) return;
    navigate(`/portal/learning/${lid}?trackId=${trackId}`);
  };

  /* MIND CFI LEXPs get a dedicated FULL-PAGE dark-themed layout. No back
     button (replaced by Exit), no description/scenario/embed surfaces, no
     pagination or per-item submit flow. The track's LEXP position drives
     the progress bar at the top. Only renders when cfi_mode is true. */
  if (lexp.cfi_mode) {
    const mcItem = items.find((i) => i.item_type === "cfi_mc");
    const likertItem = items.find((i) => i.item_type === "likert");
    const cfiAlreadyAnswered =
      (!mcItem || !!mcItem.response) && (!likertItem || !!likertItem.response) && (!!mcItem || !!likertItem);
    const positionTotal = trackLexps.length || 1;
    const positionIndex = currentLexpIdx >= 0 ? currentLexpIdx + 1 : 1;
    const positionPct = Math.round((positionIndex / positionTotal) * 100);
    const continueDisabled = !cfiAlreadyAnswered;
    const continueAction = () => {
      if (!cfiAlreadyAnswered) return;
      if (trackId) {
        if (isLastLexp && !trackAlreadySubmitted) handleSubmitTrack();
        else if (nextLexp) navigate(`/portal/learning/${nextLexp.lexp_id}?trackId=${trackId}`);
        else navigate(`/portal/tracks/${trackId}`);
      } else {
        navigate("/portal/dashboard");
      }
    };
    return (
      <LexpErrorBoundary>
      <div className="bg-white text-gray-900" data-testid="cfi-lexp-page">
        <div className="max-w-3xl mx-auto px-6 py-10">
          {loadWarning && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-body text-amber-800" data-testid="cfi-load-warning">
              {loadWarning}
            </div>
          )}
          {/* Top bar — Exit / progress / Continue */}
          <div className="flex items-center justify-between gap-4 mb-3" data-testid="cfi-top-bar">
            <button onClick={goBack} className="font-body text-base text-gray-700 hover:text-gray-900 transition-colors flex items-center gap-1" data-testid="cfi-exit-btn">
              <ArrowLeft className="h-4 w-4" /> Exit
            </button>
            <span className="font-body text-sm text-gray-500 tabular-nums" data-testid="cfi-position">
              {positionIndex} of {positionTotal}
            </span>
            <button
              onClick={continueAction}
              disabled={continueDisabled}
              data-testid="cfi-continue-btn"
              className={`font-body text-base flex items-center gap-1 transition-colors ${
                continueDisabled ? "text-gray-300 cursor-not-allowed" : "text-gray-700 hover:text-gray-900"
              }`}
            >
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          {/* Progress bar */}
          <div className="h-1 w-full rounded-full bg-gray-200 overflow-hidden mb-10" data-testid="cfi-progress-bar">
            <div className="h-full bg-blue-500 transition-all duration-300" style={{ width: `${positionPct}%` }} />
          </div>

          {/* Card */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8 sm:p-10 space-y-8 shadow-sm" data-testid="cfi-card">
            {/* MC stem + options */}
            {mcItem && (
              <div className="space-y-5" data-testid="cfi-paired-mc">
                <div className="text-lg sm:text-xl font-body font-semibold text-gray-900 leading-relaxed ql-rendered"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(mcItem.stem || mcItem.title || "") }} />
                <div className="space-y-3">
                  {((mcItem && mcItem.answer_options) || []).map((opt) => {
                    // Controlled state is authoritative — DO NOT fall back to
                    // mcItem.response so a stale prior selection can't render
                    // as pre-selected on a fresh CFI prompt.
                    const isSelected = cfiSelection.optionId === opt.option_id;
                    return (
                      <button
                        key={opt.option_id}
                        type="button"
                        disabled={cfiAlreadyAnswered || cfiSubmitting}
                        onClick={() => setCfiSelection((p) => ({ ...p, optionId: opt.option_id }))}
                        data-testid={`cfi-mc-option-${opt.option_id}`}
                        className={`w-full text-left px-5 py-4 rounded-xl border-2 font-body text-base transition-all ${
                          isSelected
                            ? "border-blue-500 bg-blue-50 text-gray-900"
                            : "border-gray-200 hover:border-gray-300 text-gray-800 bg-white"
                        } ${cfiAlreadyAnswered ? "opacity-60 cursor-not-allowed" : ""}`}
                      >
                        <span className="ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(opt.text) }} />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Divider */}
            {mcItem && likertItem && <div className="border-t border-gray-200" />}

            {/* Likert */}
            {likertItem && (
              <div className="space-y-5" data-testid="cfi-paired-likert">
                <div className="text-base font-body text-gray-700 ql-rendered"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(likertItem.stem || "How confident are you in your answer?") }} />
                <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${(likertItem.likert_scale_max ?? 5) - (likertItem.likert_scale_min ?? 1) + 1}, minmax(0, 1fr))` }}>
                  {(() => {
                    const min = likertItem.likert_scale_min ?? 1;
                    const max = likertItem.likert_scale_max ?? 5;
                    const range = [];
                    for (let i = min; i <= max; i++) range.push(i);
                    return range.map((val) => {
                      // Controlled-only — same anti-bias rule as MC.
                      const isSelected = cfiSelection.confidence === val;
                      return (
                        <button
                          key={val}
                          type="button"
                          disabled={cfiAlreadyAnswered || cfiSubmitting}
                          onClick={() => setCfiSelection((p) => ({ ...p, confidence: val }))}
                          data-testid={`cfi-likert-${val}`}
                          className={`flex flex-col items-center justify-start gap-2 px-2 py-4 rounded-xl border-2 font-body transition-all min-h-[120px] ${
                            isSelected
                              ? "border-blue-500 bg-blue-50 text-gray-900"
                              : "border-gray-200 hover:border-gray-300 text-gray-800 bg-white"
                          } ${cfiAlreadyAnswered ? "opacity-60" : ""}`}
                        >
                          <span className="text-2xl font-bold leading-none">{val}</span>
                          {likertItem.likert_labels?.[String(val)] && (
                            <span className="text-sm text-gray-600 text-center leading-tight whitespace-normal break-words">
                              {likertItem.likert_labels[String(val)]}
                            </span>
                          )}
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Submit and continue */}
            <div className="pt-2">
              {cfiAlreadyAnswered ? (
                <div className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl border-2 border-emerald-300 bg-emerald-50" data-testid="cfi-paired-completed">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  <span className="font-body text-base font-semibold text-emerald-700">Submitted — use Continue to advance</span>
                </div>
              ) : (
                <button
                  onClick={() => {
                    if (!mcItem || !likertItem) return;
                    if (cfiSelection.optionId == null || cfiSelection.confidence == null) {
                      toast.error("Select an answer and a confidence level first");
                      return;
                    }
                    handleCfiSubmit({
                      mc: { selected_option_ids: [cfiSelection.optionId] },
                      likert: { likert_value: cfiSelection.confidence, selected_option_ids: [String(cfiSelection.confidence)] },
                    });
                  }}
                  disabled={cfiSubmitting || cfiSelection.optionId == null || cfiSelection.confidence == null}
                  data-testid="cfi-paired-submit"
                  className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 font-body text-base font-semibold text-gray-900 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-gray-300 flex items-center justify-center gap-2"
                >
                  {cfiSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Submit and continue
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      </LexpErrorBoundary>
    );
  }

  return (
    <LexpErrorBoundary>
    <div
      className="flex flex-col gap-3 sm:gap-4 lg:h-[calc(100vh-9rem)] min-h-[520px]"
      data-testid="standalone-lexp-page"
    >
      {loadWarning && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-body text-amber-800" data-testid="standalone-load-warning">
          {loadWarning}
        </div>
      )}
      {/* ── Top strip: back + title + completed badge. Compact so the two
            panels claim the rest of the viewport. ── */}
      <div className="flex items-start justify-between gap-3 flex-shrink-0">
        <div className="min-w-0 flex-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={goBack}
            className="text-gray-500 hover:text-gray-700 -ml-1 mb-1"
            data-testid="lexp-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            {trackId ? "Back to Track" : "Back to Dashboard"}
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-gray-900 truncate">
              {lexp.title}
            </h1>
            {lexp.completed && (
              <Badge className="bg-green-50 text-green-600 border-green-200 text-xs font-body shrink-0">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Completed
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* ── Two-panel main area (mirrors admin presenter mode). Each panel
            scrolls internally so both remain visible without page scroll on
            large viewports. Stacks single-column on smaller ones. ── */}
      <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-3 sm:gap-4">
        {/* Left panel — description + scenario + embed. */}
        <div
          className="w-full lg:w-[60%] bg-white border border-gray-200 rounded-xl flex flex-col overflow-hidden lg:min-h-0"
          data-testid="lexp-left-panel"
        >
          <div className="px-4 sm:px-6 py-2.5 border-b border-gray-100 flex-shrink-0">
            <p className="text-[10px] font-body uppercase tracking-widest text-gray-400">
              Learning Content
            </p>
          </div>
          <div className="lg:flex-1 lg:min-h-0 px-4 sm:px-6 py-4 sm:py-5">
            <FitToPanel testId="lexp-left-fit">
            <div className="space-y-5">
            {lexp.description && (
              <div
                className="text-sm text-gray-600 font-body ql-rendered"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(lexp.description) }}
              />
            )}
            {lexp.patient_scenario && (
              <div
                className="bg-blue-50 border border-blue-200 rounded-xl p-4"
                data-testid="lexp-patient-scenario"
              >
                <div
                  className="text-sm text-gray-800 font-body ql-rendered"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(lexp.patient_scenario) }}
                />
              </div>
            )}
            {lexp.embed_enabled && lexp.embed_url && (
              <div className="rounded-xl overflow-hidden border border-gray-200">
                <iframe
                  src={lexp.embed_url}
                  title={lexp.embed_title || "Embedded content"}
                  className="w-full h-[440px]"
                />
              </div>
            )}
            {!lexp.description &&
              !lexp.patient_scenario &&
              !(lexp.embed_enabled && lexp.embed_url) && (
                <p className="text-sm text-gray-400 font-body italic text-center py-8">
                  No learning content for this experience.
                </p>
              )}
            </div>
            </FitToPanel>
          </div>
        </div>

        {/* Right panel — active question + in-panel question nav. */}
        <div
          className="w-full lg:w-[40%] bg-white border border-gray-200 rounded-xl flex flex-col overflow-hidden lg:min-h-0"
          data-testid="lexp-right-panel"
        >
          <div className="px-4 sm:px-6 py-2.5 border-b border-gray-100 flex-shrink-0 flex items-center justify-between gap-3">
            <p className="text-[10px] font-body uppercase tracking-widest text-gray-400">
              Assessment
            </p>
            {items.length > 1 && (
              <span
                className="text-[10px] font-body text-gray-400 tabular-nums"
                data-testid="lexp-item-position"
              >
                {(() => {
                  const activeIdx = items.findIndex((i) => i.id === activeItem);
                  return `${activeIdx + 1} / ${items.length}`;
                })()}
              </span>
            )}
          </div>

          <div
            className="lg:flex-1 lg:overflow-y-auto px-4 sm:px-6 py-4 sm:py-5"
            data-testid="lexp-active-item"
          >
            {items.length === 0 ? (
              <p className="text-sm text-gray-400 font-body italic text-center py-8">
                No assessment items in this experience.
              </p>
            ) : active ? (
              <>
                {(active.item_type === "mc" || active.item_type === "cfi_mc") && (
                  <McItem
                    key={active.id}
                    item={active}
                    onSubmit={(body) => handleItemSubmit(active.id, body)}
                  />
                )}
                {active.item_type === "sata" && (
                  <SataItem
                    key={active.id}
                    item={active}
                    onSubmit={(body) => handleItemSubmit(active.id, body)}
                  />
                )}
                {active.item_type === "likert" && (
                  <LikertItem
                    key={active.id}
                    item={active}
                    onSubmit={(body) => handleItemSubmit(active.id, body)}
                  />
                )}
                {active.item_type === "task" && (
                  <TaskItem
                    key={active.id}
                    item={active}
                    onSubmit={(body) => handleItemSubmit(active.id, body)}
                  />
                )}
                {active.item_type === "external_score" && (
                  <div className="text-sm text-gray-500 font-body">
                    This item is scored externally.
                  </div>
                )}
              </>
            ) : null}
          </div>

          {/* In-panel question nav — matches presenter's per-question prev/next
              at the foot of the right panel. Only shown for multi-item LEXPs
              so learners can flip between questions. On the FINAL question,
              the "Next" button is replaced with a "Submit" button that marks
              the LEXP complete and returns the learner to the track view so
              the next LEXP becomes available. */}
          {items.length > 1 && (
            <div
              className="border-t border-gray-100 px-4 py-2.5 flex items-center justify-between gap-3 flex-shrink-0"
              data-testid="lexp-item-nav"
            >
              {(() => {
                const activeIdx = items.findIndex((i) => i.id === activeItem);
                const isLastQuestion = activeIdx === items.length - 1;
                const goTo = (idx) => {
                  if (idx >= 0 && idx < items.length) setActiveItem(items[idx].id);
                };
                const handleFinalSubmit = async () => {
                  // If the LEXP isn't already marked complete (auto-complete
                  // triggers when every item has a response), mark it here.
                  // Either way, return the learner to the track view.
                  try {
                    if (!lexp?.completed) {
                      await markComplete();
                    }
                  } catch { /* markComplete already logs; still navigate */ }
                  if (trackId) navigate(`/portal/tracks/${trackId}`);
                  else navigate("/portal/dashboard");
                };
                return (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={activeIdx <= 0}
                      onClick={() => goTo(activeIdx - 1)}
                      className="h-9 px-3 sm:px-4 font-body text-xs sm:text-sm gap-1.5 border-gray-200 text-gray-700 hover:bg-gray-50"
                      data-testid="lexp-nav-prev"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <span className="text-xs font-body text-gray-400 tabular-nums">
                      {activeIdx + 1} / {items.length}
                    </span>
                    {isLastQuestion ? (
                      <Button
                        size="sm"
                        onClick={handleFinalSubmit}
                        disabled={!allAnswered}
                        title={!allAnswered ? "Answer every question before submitting" : undefined}
                        className="h-9 px-3 sm:px-4 font-body text-xs sm:text-sm gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-emerald-300 disabled:cursor-not-allowed"
                        data-testid="lexp-nav-submit"
                      >
                        Submit
                        <Send className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => goTo(activeIdx + 1)}
                        className="h-9 px-3 sm:px-4 font-body text-xs sm:text-sm gap-1.5 border-gray-200 text-gray-700 hover:bg-gray-50"
                        data-testid="lexp-nav-next"
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Bottom track-level LEXP activity navigation bar has been removed
          intentionally — learners should only navigate between questions
          within the current LEXP, never skip between activities. */}
    </div>
    </LexpErrorBoundary>
  );
}
